/**
 * Type definitions for List Items
 * 
 * This file provides TypeScript interfaces for list-related data
 * to ensure consistent typing throughout the application.
 */

/**
 * Raw list item data as stored in the database
 */
export interface ListItemData {
  id: string;
  topic_id: string;
  user_id: string;
  text: string;
  order_index: string | number;
  created_at: string;
  updated_at: string;
  metadata?: {
    client_id?: string;
    last_update?: string;
    [key: string]: any;
  };
  user_info?: {
    name?: string;
    email?: string;
    avatar_url?: string;
  };
}

/**
 * Formatted list item for use in the UI
 */
export interface FormattedListItem {
  id: string;
  topicId: string;
  userId: string;
  text: string;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
  userInfo?: {
    name: string | null;
    email: string | null;
    avatarUrl: string | null;
  };
  isOptimistic?: boolean; // Indicates if this item is an optimistic update
}

/**
 * Input for creating a new list item
 */
export interface CreateListItemInput {
  topicId: string;
  text: string;
}

/**
 * Input for updating an existing list item
 */
export interface UpdateListItemInput {
  text: string;
}

/**
 * Helper function to format a list item for consumption in UI components
 */
export function formatListItem(item: ListItemData): FormattedListItem {
  return {
    id: item.id,
    topicId: item.topic_id,
    userId: item.user_id,
    text: item.text,
    orderIndex: typeof item.order_index === 'string' 
      ? parseInt(item.order_index, 10) 
      : item.order_index as number || 0,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    userInfo: item.user_info ? {
      name: item.user_info.name || null,
      email: item.user_info.email || null,
      avatarUrl: item.user_info.avatar_url || null
    } : undefined
  };
}

/**
 * Helper function to format a list item for sending to the database
 */
export function formatListItemForDatabase(item: FormattedListItem): ListItemData {
  return {
    id: item.id,
    topic_id: item.topicId,
    user_id: item.userId,
    text: item.text,
    order_index: item.orderIndex,
    created_at: item.createdAt,
    updated_at: item.updatedAt,
    user_info: item.userInfo ? {
      name: item.userInfo.name || undefined,
      email: item.userInfo.email || undefined,
      avatar_url: item.userInfo.avatarUrl || undefined
    } : undefined
  };
}

/**
 * Helper functions for list item operations
 */

/**
 * Sort list items by order_index in ascending order
 */
export function sortByOrderIndex<T extends { order_index?: string | number } | { orderIndex?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const orderA = 'orderIndex' in a 
      ? a.orderIndex || 0 
      : (typeof a.order_index === 'string' 
        ? parseInt(a.order_index, 10) 
        : a.order_index || 0);
    
    const orderB = 'orderIndex' in b 
      ? b.orderIndex || 0 
      : (typeof b.order_index === 'string' 
        ? parseInt(b.order_index, 10) 
        : b.order_index || 0);
    
    return orderA - orderB;
  });
}

/**
 * Find the maximum order_index from a list of items
 */
export function getMaxOrderIndex<T extends { order_index?: string | number } | { orderIndex?: number }>(items: T[]): number {
  if (!items || items.length === 0) return 0;
  
  return Math.max(...items.map(item => {
    if ('orderIndex' in item) {
      return item.orderIndex || 0;
    } else {
      return typeof item.order_index === 'string' 
        ? parseInt(item.order_index, 10) 
        : item.order_index as number || 0;
    }
  }));
}